import { useMemo, useState } from 'react';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@/components/cms/ui/dialog';
import { useSceneHealth } from '@/hooks/useSceneHealth';
import { useShareLink } from '@/hooks/useShareLink';
import { useSceneAnchorCategory } from '@/hooks/useSceneAnchorCategory';
import { deviceBlockStateFromVerdicts } from '@/modules/sceneHealth';
import { SharePanel } from './SharePanel';
import { ImmersalTokenOverlay } from './ImmersalTokenOverlay';
import type { PublishDevice } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sceneInstanceId: string | null | undefined;
}

export function EditorShareDialog({ open, onOpenChange, sceneInstanceId }: Props) {
  const { data: sceneHealth, loading } = useSceneHealth(open ? sceneInstanceId : null);
  // Opening Test is what mints the draft link (share links 3.5); the gate stops a stale link flashing
  const { ready: linkReady } = useShareLink(open ? sceneInstanceId : null);
  const { anchorCategory, ready: anchorReady } = useSceneAnchorCategory(open ? sceneInstanceId : null);
  const [showTokenOverlay, setShowTokenOverlay] = useState(false);

  const contentReady = !loading && linkReady && anchorReady;

  const partialBlocks = useMemo(
    () => Object.fromEntries(deviceBlockStateFromVerdicts(sceneHealth?.verdicts).partialBlocks) as Partial<Record<PublishDevice, string>>,
    [sceneHealth],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The surface over a light, blurred scrim, at a minimum height so it keeps its size while the link loads */}
      <DialogContent scrim="soft" className="min-h-[29rem] max-w-112 bg-lureo-surface" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-lureo-16">Test your story</DialogTitle>
        </DialogHeader>

        <DialogBody className="items-center gap-5 py-6">
          {!sceneInstanceId ? (
            <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle">No story loaded.</p>
          ) : !contentReady ? (
            <div className="flex-1 flex items-center justify-center">
              <LoadingSpinner size="md" className="h-6 w-6" />
            </div>
          ) : (
            <SharePanel
              sceneInstanceId={sceneInstanceId}
              partialBlocks={partialBlocks}
              anchorCategory={anchorCategory}
              onRequestImmersalToken={() => setShowTokenOverlay(true)}
              offerSpectaclesCode
            />
          )}
        </DialogBody>

        {showTokenOverlay && (
          <ImmersalTokenOverlay onClose={() => setShowTokenOverlay(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}
